import React, { useState, useCallback } from "react";
import axios from "axios";
import { Spinner, Badge } from "reactstrap";
import PageTitleAlt2 from "../../Layout/AppMain/PageTitleAlt2";

const SEV_COLOR  = { CRITICAL: "#dc3545", WARNING: "#fd7e14", INFO: "#6c757d" };
const SEV_BG     = { CRITICAL: "#fff5f5", WARNING: "#fff8f0", INFO: "#f8f9fa" };
const SEV_ORDER  = { CRITICAL: 0, WARNING: 1, INFO: 2 };

const CHECK_LABELS = {
  call_strike_mismatch: "Call Strike Mismatch",
  call_lots_mismatch:   "Call Lots Mismatch",
  orphan_call_flag:     "Orphan Call Flag",
  csm_ref_avg_drift:    "CSM Ref Avg Drift",
  past_expiry_active:   "Past Expiry Active",
  stale_net_realized:   "Stale Net Realized",
};

const CHECK_FIX = {
  call_strike_mismatch: "Rebuild strategy to re-anchor derived.call_strike to live position",
  call_lots_mismatch:   "Rebuild strategy to sync active_call_lots from live position",
  orphan_call_flag:     "Check if call was manually closed; reset call_hedge_placed if needed",
  csm_ref_avg_drift:    "Rebuild strategy — CSM likely ran with wrong call_strike",
  past_expiry_active:   "Roll out or expire the strategy manually",
  stale_net_realized:   "Run refresh_accounting — broker cleared realised field, watermark preserved value",
};

function ValuesTable({ values }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", marginTop: 4 }}>
      {Object.entries(values).map(([k, v]) => (
        <span key={k} style={{ fontSize: 11, color: "#555" }}>
          <span style={{ color: "#888" }}>{k}: </span>
          <strong>{typeof v === "number" ? v.toLocaleString("en-US", { maximumFractionDigits: 2 }) : String(v)}</strong>
        </span>
      ))}
    </div>
  );
}

export default function AuditPage() {
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [filter, setFilter]   = useState("ALL");

  const runAudit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get("/admin/audit");
      setResult(res.data);
    } catch (e) {
      setError(e.response?.data?.error || "Audit failed");
    } finally {
      setLoading(false);
    }
  }, []);

  const severities = ["ALL", "CRITICAL", "WARNING", "INFO"];
  const issues = result?.issues?.filter(i => filter === "ALL" || i.severity === filter) || [];

  return (
    <>
      <PageTitleAlt2
        heading="Strategy Audit"
        subheading="Integrity checks across all active Pyramid Hedging strategies"
        icon="pe-7s-shield icon-gradient bg-sunny-morning"
      />

      <div style={{ padding: "0 25px 40px" }}>

        {/* Run button */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
          <button
            onClick={runAudit}
            disabled={loading}
            style={{
              padding: "10px 28px", borderRadius: 8, border: "none",
              background: loading ? "#aaa" : "#007bff", color: "#fff",
              fontWeight: 700, fontSize: 14, cursor: loading ? "default" : "pointer",
            }}
          >
            {loading ? "Running audit…" : "Run Audit"}
          </button>
          {result && !loading && (
            <span style={{ fontSize: 12, color: "#888" }}>
              Last run: {result.run_at} · {result.strategies_checked} strategies checked
            </span>
          )}
        </div>

        {error && (
          <div style={{ background: "#fff5f5", border: "1px solid #dc3545", borderRadius: 8, padding: "12px 16px", color: "#dc3545", marginBottom: 16 }}>
            {error}
          </div>
        )}

        {loading && (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spinner color="primary" />
            <div style={{ marginTop: 12, color: "#888", fontSize: 13 }}>Checking all strategies…</div>
          </div>
        )}

        {result && !loading && (
          <>
            {/* Summary cards */}
            <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
              {[
                { label: "Strategies Checked", value: result.strategies_checked, color: "#333" },
                { label: "Critical",  value: result.counts.CRITICAL, color: SEV_COLOR.CRITICAL },
                { label: "Warnings",  value: result.counts.WARNING,  color: SEV_COLOR.WARNING  },
                { label: "Info",      value: result.counts.INFO,     color: SEV_COLOR.INFO     },
              ].map(c => (
                <div key={c.label} style={{
                  background: "#fff", border: "1px solid #eee", borderRadius: 10,
                  padding: "14px 22px", minWidth: 130, textAlign: "center",
                }}>
                  <div style={{ fontSize: 26, fontWeight: 700, color: c.color }}>{c.value}</div>
                  <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{c.label}</div>
                </div>
              ))}
              {result.issues_found === 0 && (
                <div style={{
                  background: "#f0fff4", border: "1px solid #28a745", borderRadius: 10,
                  padding: "14px 22px", color: "#28a745", fontWeight: 700, fontSize: 14,
                  display: "flex", alignItems: "center",
                }}>
                  ✓ All strategies clean
                </div>
              )}
            </div>

            {/* Filter tabs */}
            {result.issues_found > 0 && (
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                {severities.map(s => {
                  const count = s === "ALL" ? result.issues_found : (result.counts[s] || 0);
                  return (
                    <button key={s} onClick={() => setFilter(s)} style={{
                      padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                      border: `1px solid ${s === "ALL" ? "#007bff" : SEV_COLOR[s] || "#ccc"}`,
                      background: filter === s ? (s === "ALL" ? "#007bff" : SEV_COLOR[s]) : "#fff",
                      color: filter === s ? "#fff" : (s === "ALL" ? "#007bff" : SEV_COLOR[s] || "#555"),
                      cursor: "pointer",
                    }}>
                      {s} {count > 0 && `(${count})`}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Issues list */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {issues.map((issue, idx) => (
                <div key={idx} style={{
                  background: SEV_BG[issue.severity] || "#fff",
                  border: `1px solid ${SEV_COLOR[issue.severity]}33`,
                  borderLeft: `4px solid ${SEV_COLOR[issue.severity]}`,
                  borderRadius: 8, padding: "12px 16px",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                    <Badge style={{ background: SEV_COLOR[issue.severity], fontSize: 10 }}>
                      {issue.severity}
                    </Badge>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>
                      {issue.username} · {issue.symbol}
                    </span>
                    <span style={{ fontSize: 13, color: "#555" }}>
                      {CHECK_LABELS[issue.check] || issue.check}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>{issue.detail}</div>
                  <ValuesTable values={issue.values} />
                  <div style={{ marginTop: 6, fontSize: 11, color: "#007bff" }}>
                    ↳ Fix: {CHECK_FIX[issue.check] || "Manual investigation required"}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}
